import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { Appeal, Complaints } from "types";
import {
  ADVOCATE_APPEAL_FEEDBACK_TYPE,
  APPEAL_STATUS,
  APPEAL_TYPE,
  FEEDBACK_TYPE2,
} from "utils/constantValues";
import { ADVOCATE_ROLES, ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import AddAppealFeedbackModal from "./AddAppealFeedbackModal";
import AddAppealRegistrationModalForwardToCouncilOffice from "./AddAppealRegistrationModalForwardToCouncilOffice";
import UpdateAppealDocModal from "./UpdateAppealDocModal";

interface AppealProps {
  complaintData: Complaints;
  appealData: Appeal[];
  onAppealRefresh: any;
}

export default function AppealCard({
  complaintData,
  appealData,
  onAppealRefresh,
}: AppealProps) {
  const { t } = useTranslation();
  const canViewAppealFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateAppealFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);

  const isRegistrar = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const isCouncilWriterAndHead = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);

  const canUpdateAppealDoc = useHasRole([ADVOCATE_ROLES.INSPECTION_PROSECUTOR]);
  const canRegisterAppealDoc = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState({});

  return (
    <div className="space-y-4">
      {/* ////////////// */}
      {appealData?.map((obj) => {
        if (
          isRegistrar
            ? ![
                APPEAL_STATUS.PENDING,
                APPEAL_STATUS.REJECTED,
                APPEAL_STATUS.REVISION_REQUIRED,
              ].includes(obj?.appeal_status || "")
            : isCouncilWriterAndHead
              ? [
                  APPEAL_STATUS.FORWARDED_TO_COUNCIL_OFFICE,
                  APPEAL_STATUS.FORWARDED_TO_COUNCIL_WRITER,
                  APPEAL_STATUS.FORWARDED_TO_COUNCIL,
                ].includes(obj?.appeal_status || "")
              : true
        ) {
          const latestFile =
            obj?.appeal_documents &&
            obj?.appeal_documents?.sort(
              (a: any, b: any) => a.created_at - b.created_at,
            )[obj?.appeal_documents?.length - 1];

          return (
            <div
              key={obj?.id}
              className="bg-white shadow-md rounded-xl p-4 w-full border-l-8 border-black mx-auto"
            >
              {/* Header */}
              {/* <div className="flex items-center gap-3 mb-4">
          <img
            src={require("../../../images/avatar.png")}
            alt="profile"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <h2 className="text-sm font-semibold">Senay Daniel</h2>
            <p className="text-xs text-gray-500">Investigation Team</p>
          </div>
        </div> */}

              {/* <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
          <p className="block text-gray-600">Uploaded Date</p>
          <p className="block text-black font-medium">
            {new Date()?.toDateString() || "-"}
          </p>
          </div>
          <hr />
           */}

              {/* Info Rows */}
              <div className="my-3">
                <p className="text-[#002855] font-medium">
                  {t("memorandumSummary")}
                </p>
                <p className="text-gray-700 mt-1">{obj?.memorandum_summary}</p>
              </div>

              <div className="mb-6 ">
                {obj?.appeal_type && (
                  <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                    <p className="block  text-gray-600">{t("appealType")}</p>
                    <p className="block  text-black font-medium">
                      {obj?.appeal_type ===
                      APPEAL_TYPE.ACCUSED_PROSECUTOR_TO_COURT
                        ? t("appealFromInspectionToCourt")
                        : t("appealFromInspectionToCouncil")}
                    </p>
                  </div>
                )}
                <hr />

                <DocumentViewerModal
                  documents={
                    isRegistrar || isCouncilWriterAndHead
                      ? latestFile
                        ? [latestFile]
                        : []
                      : obj?.appeal_documents || []
                  }
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                  layout="list"
                  label={t("accusedResponse")}
                  hideEmptyFile={true}
                />

                {obj?.registration_date && (
                  <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                    <p className="block  text-gray-600">
                      {t("registeredRegistrationDate")}
                    </p>
                    <p className="block  text-black font-medium">
                      {obj?.registration_date || "-"}
                    </p>
                  </div>
                )}

                {obj?.registration_number && (
                  <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                    <p className="block  text-gray-600">
                      {t("registeredNumber")}
                    </p>
                    <p className="block  text-black font-medium">
                      {obj?.registration_number || "-"}
                    </p>
                  </div>
                )}
              </div>

              {canViewAppealFeedback &&
                obj?.coordinator_feedbacks?.length > 0 && (
                  <div className="bg-gray-200 p-5 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium ">
                        {t("feedbackOnMemorandum")}
                      </p>
                      <StatusChip
                        status={obj?.appeal_feedback_status ?? null}
                      />
                    </div>
                    <p className="text-sm leading-relaxed">
                      {obj?.coordinator_feedbacks[0]?.comment || "-"}
                    </p>
                  </div>
                )}
              {canCreateAppealFeedback &&
                // obj?.appeal_status !== APPEAL_STATUS.APPROVED &&
                obj?.appeal_feedback_status !==
                  ADVOCATE_APPEAL_FEEDBACK_TYPE.APPROVED && (
                  // !obj?.coordinator_feedbacks?.length &&
                  <button
                    className="w-full bg-[#5f5f5f] hover:bg-gray-800 border border-white text-white py-2 rounded-lg mt-2"
                    onClick={() => {
                      setSelectedAppeal(obj);
                      setIsFeedbackOpen(true);
                    }}
                  >
                    {t("addFeedbackOnMemorandum")}
                  </button>
                )}

              {canRegisterAppealDoc &&
                // obj?.appeal_status === APPEAL_STATUS.FORWARDED_TO_REGISTRAR &&
                !obj?.registration_number && (
                  <div className="text-right mt-3">
                    <button
                      className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
                      type="submit"
                      onClick={() => {
                        setSelectedAppeal(obj);
                        setIsOpen(true);
                      }}
                    >
                      {t("addRegistrationNo")}
                    </button>
                  </div>
                )}

              {canUpdateAppealDoc &&
                obj?.appeal_feedback_status === "respond_back" &&
                !obj?.registration_number && (
                  <div className="text-right mt-3">
                    <button
                      className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
                      type="submit"
                      onClick={() => {
                        setSelectedAppeal(obj);
                        setIsUpdateOpen(true);
                      }}
                    >
                      {t("updateMemorandum")}
                    </button>
                  </div>
                )}

              {complaintData && (
                <AddAppealFeedbackModal
                  isOpen={isFeedbackOpen}
                  setIsOpen={setIsFeedbackOpen}
                  complaintData={complaintData}
                  appealData={selectedAppeal}
                  onRefresh={onAppealRefresh}
                />
              )}

              {selectedAppeal && isOpen && (
                <AddAppealRegistrationModalForwardToCouncilOffice
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  complaintData={complaintData}
                  appealData={selectedAppeal}
                  onRefresh={onAppealRefresh}
                />
              )}

              {selectedAppeal && isUpdateOpen && (
                <UpdateAppealDocModal
                  isOpen={isUpdateOpen}
                  setIsOpen={setIsUpdateOpen}
                  complaintData={complaintData}
                  appealData={selectedAppeal}
                  onRefresh={onAppealRefresh}
                />
              )}
            </div>
          );
        } else return null;
      })}
    </div>
  );
}
